import { useAppDispatch, useAppSelector } from "./redux";
import { setAuthState } from "../redux/Auth/auth.slice";
import { useAppNavigate } from "./router";
import { setClinicSliceReset } from "../redux/Clinic/clinic.slice";
import { IlocalStorageProvider } from "../hexagon/adapters/driven/providers/localStorageProvider";
import { IPushNotificationProvider } from "../hexagon/adapters/driven/providers/pushNotificationProvider";
import { IAuthController } from "../hexagon/adapters/driver/authController";
import { IAnalyticsController } from "../hexagon/adapters/driver/analyticsController";
import { IAppNotification } from "../types/app-notification";
import { AppNotificationType } from "../constants/appNotification";
import { setLayoutState, setMenuOpen } from "../redux/Layout/layout.slice";
import { setDashboardStateReset } from "../redux/Dashboard/dashboard.slice";
import type { ISendZapierTrigger } from "../util/zapierMain";
import { setRecentlyReviewedReset } from "../redux/Patient/patient.slice";
import { preloadAgoraSDK } from "../utils/agoraLoader";

export const useAuth = ({
  authController,
  localStorageProvider,
  pushNotificationProvider,
  populateAppData,
  analyticsController,
  appNotification,
  circadiaEngineProvider,
  sendZapierTrigger,
}: {
  authController: IAuthController;
  localStorageProvider: IlocalStorageProvider;
  pushNotificationProvider: IPushNotificationProvider;
  analyticsController: IAnalyticsController;
  populateAppData: () => Promise<void>;
  appNotification: IAppNotification;
  circadiaEngineProvider: ICircadiaEngineProvider;
  sendZapierTrigger: ISendZapierTrigger;
}) => {
  const authState = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const onBiometric = async () => {
    // dispatch(
    //   setAuthState({
    //     authState: "loading",
    //   })
    // );
    // dispatch(
    //   setLayoutState({
    //     layoutState: "loading",
    //   })
    // );
    const { success, error } = await authController.verifyBio({
      email: localStorageProvider.get("userEmail")!,
      key: "pin",
    });

    if (success) {
      await pushNotificationProvider.registerDeviceForPushNotifications();
      let userInfo = authState.user;
      if (!userInfo) {
        const { data } = await authController.getCurrentUser();
        if (data?.user) userInfo = data.user;
      }
      dispatch(
        setAuthState({
          ...authState,
          user: userInfo,
          isPinAuthorized: true,
        })
      );
      await populateAppData();

      // dispatch(
      //   setAuthState({
      //     authState: "pending",
      //   })
      // );
      // dispatch(
      //   setLayoutState({
      //     layoutState: "pending",
      //   })
      // );
      const sendWelcome = localStorageProvider.get("sendWelcome");
      if (sendWelcome === "2") {
        console.log("Send Welcome!!");
        sendZapierTrigger({
          email: userInfo!.email,
          name:
            userInfo!.fullName ||
            `${userInfo!.lastName}, ${userInfo!.firstName}`,
        });
      }
      localStorageProvider.remove("sendWelcome");

      localStorageProvider.remove("idle");

      // Preload Agora SDK in background for video calls
      preloadAgoraSDK();
    }
    // dispatch(
    //   setAuthState({
    //     authState: "pending",
    //   })
    // );
    // dispatch(
    //   setLayoutState({
    //     layoutState: "pending",
    //   })
    // );

    if (error) {
      appNotification({
        nTitle: `${error}`,
        nType: AppNotificationType.ERROR,
      });
    }
  };

  const onPinSetup = async () => {
    await pushNotificationProvider.registerDeviceForPushNotifications();
    let userInfo = authState.user;
    if (!userInfo) {
      const { data } = await authController.getCurrentUser();
      if (data?.user) userInfo = data.user;
    }
    dispatch(
      setAuthState({
        ...authState,
        user: userInfo,
        isPinAuthorized: true,
      })
    );
    await populateAppData();

    const sendWelcome = localStorageProvider.get("sendWelcome");
    if (sendWelcome === "2") {
      sendZapierTrigger({
        email: userInfo!.email,
        name:
          userInfo!.fullName || `${userInfo!.lastName}, ${userInfo!.firstName}`,
      });
    }
    localStorageProvider.remove("sendWelcome");

    localStorageProvider.remove("idle");
    analyticsController.logEvent(
      "Valid PIN entered",
      {
        email: authState.user?.email,
      },
      authState.user?.email
    );

    // Preload Agora SDK in background for video calls
    preloadAgoraSDK();
  };

  const onLogout = async (isCompleteScreen?: boolean) => {
    dispatch(
      setAuthState({
        authState: "loading",
      })
    );

    const { data } = await authController.getCurrentUser();
    if (isCompleteScreen) {
      analyticsController.logEvent("Tap on login again button", {
        email: data?.user?.email,
      });
    }

    await pushNotificationProvider.unregisterDeviceForPushNotifications();

    await authController.logoutUser();

    dispatch(
      setAuthState({
        user: undefined,
        isAuthorized: false,
        isPinAuthorized: false,
      })
    );
    localStorageProvider.remove("internalUse");
    localStorageProvider.remove("saveSSvalue");
    localStorageProvider.remove("username");
    localStorageProvider.remove("isBiometric");
    localStorageProvider.remove("isNewPassword");
    localStorageProvider.remove("sso");
    localStorageProvider.remove("accessToken");
    localStorageProvider.remove("zpax"); // Z-Pax cleanup
    localStorageProvider.remove("idToken"); // Z-Pax cleanup
    localStorageProvider.remove("refreshToken"); // Z-Pax cleanup
    localStorageProvider.remove("dashboard_filters");
    localStorageProvider.remove("userInfoUpdated");
    localStorageProvider.remove("firstName");
    localStorageProvider.remove("lastName");
    if (!isCompleteScreen) {
      localStorageProvider.remove("isNewUser");
    }
    dispatch(setClinicSliceReset());
    dispatch(setRecentlyReviewedReset());
    dispatch(setLayoutState({ pageTabs: {} }));
    dispatch(setDashboardStateReset());
    dispatch(setMenuOpen(false));
    circadiaEngineProvider.logoutIntercomUser();
    navigate("/login", { replace: true });
    analyticsController.logEvent("Tap on Logout", {
      email: data?.user?.email,
      cUserId: data?.user?.userID,
    });
    analyticsController.resetEvent();
    navigate("/login", { replace: true });
    dispatch(
      setAuthState({
        authState: "pending",
      })
    );
    return true;
  };

  const onSwitchAccount = async () => {
    dispatch(
      setAuthState({
        authState: "loading",
      })
    );

    const email = localStorageProvider.get("userEmail");
    analyticsController.logEvent("Tap on switch account", {
      email,
    });

    await authController.logoutUser();
    dispatch(
      setAuthState({
        user: undefined,
        isAuthorized: false,
        isPinAuthorized: false,
      })
    );
    localStorageProvider.remove("internalUse");
    localStorageProvider.remove("saveSSvalue");
    localStorageProvider.remove("username");
    localStorageProvider.remove("isBiometric");
    localStorageProvider.remove("isNewPassword");
    localStorageProvider.remove("isNewUser");
    // localStorageProvider.remove("zpax"); // Z-Pax cleanup
    // localStorageProvider.remove("idToken"); // Z-Pax cleanup
    dispatch(setClinicSliceReset());
    dispatch(setLayoutState({ pageTabs: {} }));
    dispatch(setDashboardStateReset());
    dispatch(setRecentlyReviewedReset());
    circadiaEngineProvider.logoutIntercomUser();
    analyticsController.resetEvent();
    navigate("/login", { replace: true });
    dispatch(
      setAuthState({
        authState: "pending",
      })
    );
  };

  const onTokenExpire = async () => {
    await authController.logoutUser();
    const email = localStorageProvider.get("userEmail");
    dispatch(
      setAuthState({
        authState: "pending",
        user: undefined,
        isAuthorized: false,
        isPinAuthorized: false,
      })
    );
    localStorageProvider.remove("internalUse");
    localStorageProvider.remove("saveSSvalue");
    localStorageProvider.remove("username");
    localStorageProvider.remove("isBiometric");
    localStorageProvider.remove("isNewPassword");
    localStorageProvider.remove("isNewUser");
    // localStorageProvider.remove("zpax"); // Z-Pax cleanup
    // localStorageProvider.remove("idToken"); // Z-Pax cleanup
    dispatch(setClinicSliceReset());
    dispatch(setLayoutState({ pageTabs: {} }));
    dispatch(setDashboardStateReset());
    dispatch(setRecentlyReviewedReset());
    circadiaEngineProvider.logoutIntercomUser();
    navigate("/login", { replace: true });
    analyticsController.logEvent("On Token Expire", {
      email,
    });
    analyticsController.resetEvent();
    navigate("/login", { replace: true });
  };

  return { onPinSetup, onLogout, onBiometric, onTokenExpire, onSwitchAccount };
};
